import { ethers } from 'ethers';
// Import centralized utilities and re-export for backward compatibility
import { formatAddress, formatEther } from '../../../shared/utils';
export { formatAddress, formatEther };

interface EthereumProvider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
  isMetaMask?: boolean;
}

declare global {
  interface Window {
    ethereum?: EthereumProvider;
  }
}

export interface WalletInfo {
  address: string;
  chainId: number;
  balance?: string;
}

/**
 * Check if MetaMask is installed
 */
export function isMetaMaskInstalled(): boolean {
  return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined';
}

/**
 * Get the current wallet provider
 */
export function getWalletProvider() {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed');
  }
  return new ethers.BrowserProvider(window.ethereum as ethers.Eip1193Provider);
}

/**
 * Connect to wallet and return wallet info
 */
export async function connectWallet(): Promise<WalletInfo> {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
  }

  try {
    // Request account access
    const accountsResult = await window.ethereum!.request({
      method: 'eth_requestAccounts',
    });

    const accounts = accountsResult as string[];
    if (!accounts || accounts.length === 0) {
      throw new Error('No accounts found. Please connect your wallet.');
    }

    const address = accounts[0];

    // Get chain ID
    const chainIdResult = await window.ethereum!.request({
      method: 'eth_chainId',
    });
    const chainId = chainIdResult as string;

    // Get balance
    const provider = getWalletProvider();
    const balance = await provider.getBalance(address);

    return {
      address,
      chainId: parseInt(chainId, 16),
      balance: ethers.formatEther(balance),
    };
  } catch (error: unknown) {
    console.error('Wallet connection failed:', error);

    const errorCode = (error as { code?: number }).code;
    const errorMessage = error instanceof Error ? error.message : 'Failed to connect wallet.';

    if (errorCode === 4001) {
      throw new Error('Wallet connection was rejected by the user.');
    } else if (errorCode === -32002) {
      throw new Error('Wallet connection request is already pending. Please check your wallet.');
    }

    throw new Error(errorMessage);
  }
}

/**
 * Sign a message with the connected wallet
 */
export async function signMessage(message: string, walletAddress?: string): Promise<string> {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed');
  }

  try {
    const provider = getWalletProvider();
    const signer = await provider.getSigner();

    // Verify the signer address matches the expected wallet address
    if (walletAddress) {
      const signerAddress = await signer.getAddress();
      if (signerAddress.toLowerCase() !== walletAddress.toLowerCase()) {
        throw new Error('Signer address does not match the expected wallet address');
      }
    }

    return await signer.signMessage(message);
  } catch (error: unknown) {
    console.error('Message signing failed:', error);

    const errorCode = (error as { code?: number }).code;
    const errorMessage = error instanceof Error ? error.message : '';

    if (errorCode === 4001 || errorMessage.includes('User rejected')) {
      throw new Error('Message signing was rejected by the user.');
    }

    throw new Error(errorMessage || 'Failed to sign message.');
  }
}

/**
 * Switch to a specific network
 */
export async function switchNetwork(chainId: number): Promise<void> {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed');
  }

  try {
    await window.ethereum!.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: `0x${chainId.toString(16)}` }],
    });
  } catch (error: unknown) {
    const errorCode = (error as { code?: number }).code;
    const errorMessage = error instanceof Error ? error.message : 'Failed to switch network.';

    // This error code indicates that the chain has not been added to MetaMask
    if (errorCode === 4902) {
      throw new Error(`Network with chain ID ${chainId} is not added to your wallet.`);
    }

    throw new Error(errorMessage);
  }
}

/**
 * Add a custom token to the wallet
 */
export async function addToken(
  tokenAddress: string,
  tokenSymbol: string,
  tokenDecimals: number,
): Promise<void> {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed');
  }

  try {
    await window.ethereum!.request({
      method: 'wallet_watchAsset',
      params: [
        {
          type: 'ERC20',
          options: {
            address: tokenAddress,
            symbol: tokenSymbol,
            decimals: tokenDecimals,
          },
        },
      ],
    });
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to add token to wallet.';
    throw new Error(errorMessage);
  }
}

/**
 * Get the current account address
 */
export async function getCurrentAccount(): Promise<string | null> {
  if (!isMetaMaskInstalled()) {
    return null;
  }

  try {
    const accountsResult = await window.ethereum!.request({
      method: 'eth_accounts',
    });

    const accounts = accountsResult as string[];
    return accounts && accounts.length > 0 ? accounts[0] : null;
  } catch (error) {
    console.error('Failed to get current account:', error);
    return null;
  }
}

/**
 * Listen for account changes
 */
export function onAccountsChanged(callback: (_accounts: string[]) => void): () => void {
  if (!isMetaMaskInstalled()) {
    return () => {};
  }

  const handleAccountsChanged = (...args: unknown[]) => {
    const accounts = args[0] as string[];
    callback(accounts);
  };

  window.ethereum!.on('accountsChanged', handleAccountsChanged);

  // Return cleanup function
  return () => {
    if (window.ethereum?.removeListener) {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
    }
  };
}

/**
 * Listen for chain changes
 */
export function onChainChanged(callback: (_chainId: string) => void): () => void {
  if (!isMetaMaskInstalled()) {
    return () => {};
  }

  const handleChainChanged = (...args: unknown[]) => {
    const chainId = args[0] as string;
    callback(chainId);
  };

  window.ethereum!.on('chainChanged', handleChainChanged);

  // Return cleanup function
  return () => {
    if (window.ethereum?.removeListener) {
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    }
  };
}

/**
 * Format wallet address for display
 */

/**
 * Validate Ethereum address format
 */
export function isValidAddress(address: string): boolean {
  try {
    return ethers.isAddress(address);
  } catch {
    return false;
  }
}

/**
 * Convert Wei to Ether
 */

/**
 * Convert Ether to Wei
 */
export function parseEther(ether: string): bigint {
  try {
    return ethers.parseEther(ether);
  } catch {
    return BigInt(0);
  }
}
